/** In-app manuals. Keys match i18n language codes. English is the fallback. */

const en = {
  client: {
    sections: [
      {
        id: 'trips',
        title: 'Trips',
        articles: [
          {
            id: 'create',
            title: 'Create a trip',
            summary: 'Start a trip you organize yourself.',
            steps: [
              'Open My Trips and choose New trip, or open Plan.',
              'Enter a name, a destination, and a start date. The end date must be the same day or later.',
              'Save. The trip opens with the itinerary, members, chat, map, checklist, expenses, documents, and memories.',
              'You are the organizer. You can edit the shared itinerary, invite people, and remove the trip.',
            ],
          },
          {
            id: 'invite',
            title: 'Invite people',
            summary: 'Add travelers by email from the Members tab.',
            steps: [
              'Open the trip and choose the Members tab.',
              'Choose Invite member and enter their email.',
              'They receive an invitation. Until they accept, the trip stays on your list and you can still plan stops for them on Personal itinerary if they already have an account.',
              'After they accept, the trip appears in their My Trips and they can use chat, expenses, and their own checklist.',
            ],
          },
          {
            id: 'itinerary',
            title: 'Add to the shared itinerary',
            summary: 'The Trip itinerary is the plan everyone on the trip can read.',
            steps: [
              'Open the trip and stay on Itinerary. The first sub-tab is Trip itinerary.',
              'Choose Add activity. Enter a name, place, and start and end time.',
              'Save. The stop shows on that day for every member.',
              'On a trip you organize, you can edit or delete a stop. Drag the handle to change the order on that day, or drop the stop on another day. The stop keeps its times on the day you drop it.',
              'If an advisor manages the trip, only the advisor can add, edit, or drag stops on Trip itinerary.',
            ],
          },
          {
            id: 'personal',
            title: 'Personal itinerary',
            summary: 'Your own stops, separate from the shared plan.',
            steps: [
              'On Itinerary, open Personal itinerary and choose Personal.',
              'Choose Add activity. The stop is saved on your plan only.',
              'You can drag your own timed stops when a day has two or more. Other members cannot move your plan.',
              'On a group trip the advisor can also add and move stops while viewing your Personal tab. They can do this after inviting you, even before you accept.',
            ],
          },
          {
            id: 'chat',
            title: 'Chat',
            summary: 'Message everyone on the trip.',
            steps: [
              'Open the trip and choose the Chat tab.',
              'Type a message and send it. Everyone who has joined the trip can read it.',
              'Chat is the trip conversation. A private note to your advisor belongs in Requests.',
            ],
          },
          {
            id: 'expenses',
            title: 'Share expenses',
            summary: 'Record a cost and choose who can see it.',
            steps: [
              'Open the trip and choose Expenses.',
              'Add a description and an amount.',
              'On a trip you organize with friends, members of that trip can see the expenses and who owes what.',
              'On a trip an advisor manages, or on your copy of a group trip, pick the people to share it with. Only you and those people see that expense. It is not shown to the whole group.',
            ],
          },
          {
            id: 'checklist',
            title: 'Checklist',
            summary: 'Your packing and to-do list for this trip.',
            steps: [
              'Open the trip and choose Checklist.',
              'Check items off as you finish them. Add your own items when the list allows it.',
              'The list belongs to this trip only. On a group trip it is not the advisor’s master list, and the advisor does not see it.',
            ],
          },
          {
            id: 'documents',
            title: 'Documents',
            summary: 'Files for this trip.',
            steps: [
              'Open the trip and choose Documents.',
              'Upload a file, such as a ticket or confirmation.',
              'People on the trip can open the files stored here.',
            ],
          },
          {
            id: 'memories',
            title: 'Memories',
            summary: 'Photos from the trip.',
            steps: [
              'Open the trip and choose Memories, or open Memories from the menu.',
              'Create an album and add photos.',
              'Albums stay with the trip so the group can look back later.',
            ],
          },
          {
            id: 'map',
            title: 'Map',
            summary: 'See the shared plan on a map.',
            steps: [
              'On a trip you organize, open the Map tab.',
              'Stops with a place appear on the map.',
              'The Map tab is hidden when an advisor manages the trip.',
            ],
          },
        ],
      },
      {
        id: 'groupTrips',
        title: 'Group trip',
        articles: [
          {
            id: 'open',
            title: 'What you open',
            summary: 'You use your own copy of the group trip.',
            steps: [
              'The advisor keeps a master trip. You never edit that master.',
              'Open the trip from My Trips. That page is your copy: your checklist, your expenses, and your personal stops.',
              'The shared days are still on Itinerary under Trip itinerary. You can read them. You cannot add, edit, or drag those stops.',
            ],
          },
          {
            id: 'personal',
            title: 'Your plan on a group trip',
            summary: 'Add and rearrange only your personal stops.',
            steps: [
              'Open Itinerary, then Personal itinerary, then Personal.',
              'Add an activity. It stays on your copy.',
              'Drag the handle when a day has two timed stops. Only you, and the advisor while they are looking at your plan, can move those stops.',
            ],
          },
          {
            id: 'expenses',
            title: 'Expenses on a group trip',
            summary: 'Costs stay between the people you pick.',
            steps: [
              'Open Expenses on your copy of the trip.',
              'Add the cost and choose who to share it with.',
              'Other travelers do not see it. The advisor sees an expense only when they created it or you were assigned to it.',
            ],
          },
          {
            id: 'addons',
            title: 'Add-ons',
            summary: 'Extras the advisor offers, for everyone or just for you.',
            steps: [
              'Open the Add-ons tab on a trip your advisor manages.',
              'For everyone lists extras offered to the whole group. Just for you lists extras the advisor attached only to you.',
              'Choose Add to trip to pay inside the app. If the extra has a payment link, a second button opens the advisor’s checkout in a new tab.',
            ],
          },
          {
            id: 'billing',
            title: 'Billing',
            summary: 'The invoice from your advisor.',
            steps: [
              'The Billing tab appears only when an advisor manages the trip. It is hidden on a trip you organize yourself.',
              'Until they send an invoice, the tab explains that nothing has been sent yet.',
              'When an invoice is there, review the lines and pay the balance or a scheduled amount. This invoice is separate from Expenses you share with other travelers.',
            ],
          },
          {
            id: 'chat',
            title: 'Chat',
            summary: 'Talk with the people on your trip.',
            steps: [
              'Open Chat on your trip.',
              'Send a message. People who have joined can read it.',
              'The advisor also has a group chat on their master trip. Use your Chat tab for the conversation on your copy.',
            ],
          },
        ],
      },
      {
        id: 'packages',
        title: 'Packages',
        articles: [
          {
            id: 'browse',
            title: 'Browse a package',
            summary: 'Read what an advisor is selling before you book.',
            steps: [
              'Open Packages from the menu.',
              'Open a package to read the title, dates, price, what is included, and the day-by-day plan.',
              'Use the actions on that page to inquire or book when the advisor has opened them.',
            ],
          },
          {
            id: 'after',
            title: 'After you inquire or book',
            summary: 'The thread and the booking live in different places.',
            steps: [
              'A question you send about a package shows up under Requests. Open the thread to keep writing to the advisor.',
              'A booking shows under My Bookings with its status and payment.',
              'If the advisor turns the package into a trip for you, that trip appears in My Trips and follows the trip instructions above.',
            ],
          },
        ],
      },
      {
        id: 'inbox',
        title: 'Inbox',
        articles: [
          {
            id: 'message',
            title: 'Message an advisor',
            summary: 'Requests is your inbox.',
            steps: [
              'Open Requests from the menu.',
              'Choose a thread to read the history.',
              'Type a reply in the box and send it. The advisor sees the same thread in their inbox.',
              'Start from a package or a trip when you need a new conversation, then come back here to continue it.',
            ],
          },
        ],
      },
      {
        id: 'bookings',
        title: 'Bookings',
        articles: [
          {
            id: 'view',
            title: 'Check a booking',
            summary: 'Status and payment for something you already bought.',
            steps: [
              'Open My Bookings from the menu.',
              'Open a booking to see what it covers, the status, and what has been paid.',
              'Trip costs your advisor invoices later are on the trip’s Billing tab, not in this list.',
            ],
          },
        ],
      },
    ],
  },
  partner: {
    sections: [
      {
        id: 'trips',
        title: 'Client trips',
        articles: [
          {
            id: 'create',
            title: 'Create a trip',
            summary: 'Open a file for one client.',
            steps: [
              'From the partner home, choose New trip.',
              'Leave the trip as a client trip (not a group) unless you mean to build a master with one copy per traveler.',
              'Enter the name, destination, and dates. The end date must be on or after the start date.',
              'Save. The trip is managed by you. The client cannot edit the shared itinerary.',
            ],
          },
          {
            id: 'file',
            title: 'What is on the trip page',
            summary: 'Manage itinerary opens the client file.',
            steps: [
              'On Trips, open the card and choose Manage itinerary.',
              'The tabs are Itinerary, Operations, Members, Documents, Add-ons, Expenses, and History.',
              'Use the status control to move the trip through your pipeline, and the assignee control to hand it to a teammate.',
            ],
          },
          {
            id: 'invite',
            title: 'Invite the client',
            summary: 'They can be planned for before they accept.',
            steps: [
              'Open Members and invite them by email.',
              'If they already have an account, you can add personal stops for them before they accept the invite.',
              'After they accept, the trip shows in their My Trips. They still cannot change the shared itinerary.',
            ],
          },
          {
            id: 'itinerary',
            title: 'Build the itinerary',
            summary: 'Only you can change the shared plan.',
            steps: [
              'On Itinerary, choose Add activity and enter the name, place, and times.',
              'Drag a stop by its handle to reorder it on that day, or drop it on another day, including a day with nothing planned. It keeps its start and end times on the new day.',
              'Travelers can read this plan. They cannot add, edit, or drag it.',
              'Use Suggest with AI when your plan includes it. Download PDF or Email itinerary from the header when you want to send the plan. Publish guide when travelers should see stops that were still in draft.',
            ],
          },
          {
            id: 'personal',
            title: 'Personal itinerary',
            summary: 'Stops that belong to the client, not the shared plan.',
            steps: [
              'Open the client’s itinerary and switch to their personal plan.',
              'Add an activity. It is stored on their trip, not on the shared days.',
              'You can drag their timed stops while you are viewing their plan. They can also add and drag their own stops. Other clients cannot.',
            ],
          },
          {
            id: 'addons',
            title: 'Offer an add-on',
            summary: 'Create one, or pick from your lists.',
            steps: [
              'Open the Add-ons tab on the client trip.',
              'New add-on creates a catalog item and offers it on this trip. From add-ons picks an item you already saved. From inventory picks a room, ticket, or other inventory item.',
              'Choose whether they are charged on the trip bill or can buy it themselves.',
              'The client sees it on their Add-ons tab. If the item has a payment link, they also get a button that opens your checkout.',
            ],
          },
          {
            id: 'expenses',
            title: 'Assign an expense',
            summary: 'A cost you record for this client.',
            steps: [
              'Open Expenses on the client trip.',
              'Add the description and amount and assign it to the client.',
              'They see expenses you assign to them. They can also record a private expense and share it only with people they choose. You do not see those unless you are included.',
            ],
          },
          {
            id: 'documents',
            title: 'Documents',
            summary: 'Files on this client’s trip.',
            steps: [
              'Open Documents.',
              'Upload confirmations, visas, or other files the client should have with the trip.',
            ],
          },
          {
            id: 'operations',
            title: 'Operations and history',
            summary: 'Day-of shortcuts, open requests, and the change log.',
            steps: [
              'Open Operations for shortcuts: add an activity, download the PDF, email the itinerary, message the client, or open documents.',
              'The same tab lists open requests and a timeline for the trip.',
              'Open History to see what changed on the trip and who changed it.',
            ],
          },
        ],
      },
      {
        id: 'groupTrips',
        title: 'Group trip',
        articles: [
          {
            id: 'create',
            title: 'Create a group trip',
            summary: 'One master, plus a copy for each traveler.',
            steps: [
              'Choose New trip and create it as a group trip. Your plan must include group trips.',
              'The card on Trips is marked Group trip. Choose Manage group trip.',
              'Tabs on the master are Itinerary, Travelers, Group billing, Documents, Chat, Add-ons, and Expenses. There is no checklist on the master.',
            ],
          },
          {
            id: 'itinerary',
            title: 'Master itinerary',
            summary: 'The plan every traveler reads. Only you edit it.',
            steps: [
              'On the master trip, stay on Itinerary.',
              'Add activities and drag them when a day has two timed stops.',
              'Publish guide when the shared days should be visible to travelers. Download a PDF for one traveler from the header menu when you need their copy.',
              'Travelers open their own trip and can read these stops. They cannot change them.',
            ],
          },
          {
            id: 'travelers',
            title: 'Travelers and personal plans',
            summary: 'Each person has a client itinerary.',
            steps: [
              'Open Travelers to see who is on the group.',
              'Open Client itinerary for a person, then Personal, and pick their name.',
              'Add a stop for them even if they have not accepted the invitation yet, as long as the invite is tied to their account. An email with no account yet cannot own a personal stop.',
              'They can add and drag stops on their own Personal tab. You can do the same while their plan is selected. They cannot drag the master itinerary.',
            ],
          },
          {
            id: 'addonsEveryone',
            title: 'Add-ons for everyone',
            summary: 'The master Add-ons tab copies the extra onto each traveler.',
            steps: [
              'Open Add-ons on the master trip. You need at least one traveler on the group.',
              'Use New add-on, From add-ons, or From inventory. There is no picker here for a single person.',
              'Saving offers that extra to every traveler. A notice tells you how many copies were created.',
              'Each traveler sees it under For everyone on their Add-ons tab.',
            ],
          },
          {
            id: 'addonsOne',
            title: 'Add-on for one person',
            summary: 'Create it from that traveler’s client itinerary.',
            steps: [
              'Open Client itinerary, choose Personal, and select the traveler.',
              'Choose Add-on for their name to create a new extra, or From add-ons to attach one you already saved.',
              'It is offered only on their trip. They see it under Just for you. The rest of the group does not.',
            ],
          },
          {
            id: 'expenses',
            title: 'Expenses',
            summary: 'Assign a cost to one or more travelers.',
            steps: [
              'Open Expenses on the master trip.',
              'Create the expense and assign the travelers who should share it. The amount is split across the people you select.',
              'Each person sees only their share on their own trip. They can also add a private expense and share it with another traveler. That private expense stays between those people.',
            ],
          },
          {
            id: 'billing',
            title: 'Group billing',
            summary: 'Invoices for the trip. Separate from Expenses.',
            steps: [
              'Open Group billing on the master trip.',
              'Add payers and charges, then send or record payment. You can store a payment link on a charge.',
              'After you send an invoice, the traveler sees it on the Billing tab of their trip. Until then that tab is empty on purpose.',
              'Do not put peer costs here. Those belong on Expenses.',
            ],
          },
          {
            id: 'chat',
            title: 'Chat and documents',
            summary: 'The master conversation and files.',
            steps: [
              'Open Chat on the master trip to message the group from your side.',
              'Open Documents to store files for the group.',
              'Travelers also have Chat and Documents on their own copy.',
            ],
          },
        ],
      },
      {
        id: 'packages',
        title: 'Packages',
        articles: [
          {
            id: 'create',
            title: 'Create a package',
            summary: 'A trip product clients can browse.',
            steps: [
              'Open Packages and choose New package.',
              'Enter the title, description, price, and dates. Add the day-by-day plan travelers should see.',
              'Save it as a draft while you are still editing. Publish it when it should appear to clients.',
            ],
          },
          {
            id: 'trip',
            title: 'Turn a package into a trip',
            summary: 'Copy the package plan into a client or group trip.',
            steps: [
              'On the package, use the action that creates a trip from the package.',
              'The new trip opens in the trip builder with the package title, dates, and plan filled in.',
              'Finish the dates and client, then manage it like any other trip.',
            ],
          },
        ],
      },
      {
        id: 'catalog',
        title: 'Inventory and add-ons',
        articles: [
          {
            id: 'inventory',
            title: 'Add an inventory item',
            summary: 'Rooms, tickets, and other things you can place on a trip.',
            steps: [
              'Open Inventory and create an item.',
              'Set the name, price, and how the price is charged.',
              'Optional: add a payment link. It must start with https. Add a short label if you do not want the button to say Pay now.',
              'Offer it on a trip with From inventory. The link is copied onto that offer.',
            ],
          },
          {
            id: 'addons',
            title: 'Add an add-on to your catalog',
            summary: 'The list behind From add-ons and New add-on.',
            steps: [
              'Open Add-ons in the sidebar and create an item, or create one with New add-on on a trip.',
              'Set the name, price, and an optional https payment link and label.',
              'On a client trip, offer it from the Add-ons tab. On a group master, offering it there copies it to every traveler. For one person, offer it from their Client itinerary.',
            ],
          },
        ],
      },
      {
        id: 'inbox',
        title: 'Inbox',
        articles: [
          {
            id: 'message',
            title: 'Reply to a message',
            summary: 'Inbox and Request are the same threads the client sees under Requests.',
            steps: [
              'Open Inbox or Request from the sidebar.',
              'Choose a thread and read the history.',
              'Type a reply and send it. The client sees it in Requests.',
              'When the conversation should become a trip, start a trip from that inquiry so the dates and client carry over.',
            ],
          },
        ],
      },
      {
        id: 'bookings',
        title: 'Bookings and clients',
        articles: [
          {
            id: 'bookings',
            title: 'Bookings',
            summary: 'Package purchases and their payments.',
            steps: [
              'Open Bookings.',
              'Open a row to see the traveler, the package, the status, and what has been paid.',
              'Trip invoices you send later are on the trip’s Group billing or the client’s Billing tab, not only in this list.',
            ],
          },
          {
            id: 'clients',
            title: 'Clients and team',
            summary: 'People you serve, and people who work with you.',
            steps: [
              'Open Clients to see travelers and jump to their trips.',
              'Open Team to invite a coworker. After they join, you can assign them a trip from the trip’s assignee control.',
              'Billing in the sidebar is your agency subscription. Traveler invoices are Group billing on a group trip, or the invoice the client opens on their Billing tab.',
            ],
          },
        ],
      },
    ],
  },
};

const MANUALS = { en };

const normalizeLanguage = (language) => {
  const value = String(language || 'en');
  if (MANUALS[value]) return value;
  const base = value.split('-')[0];
  return MANUALS[base] ? base : 'en';
};

export const howToManual = (language, role) => {
  const pack = MANUALS[normalizeLanguage(language)] || en;
  return role === 'partner' ? pack.partner : pack.client;
};

export const howToManualLanguages = () => Object.keys(MANUALS);
